import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Monitor,
  Users,
  Database,
  Clock,
  Trash2,
  Smartphone,
  Palette,
  Mic,
  Sparkles,
} from 'lucide-react';
import { HubShell } from '@/components/layout/HubShell';
import { TabMonitor } from './TabMonitor';
import { TabCollaborationHub } from './TabCollaborationHub';
import TabMemoryPage from './TabMemory';
import TabTimeMachinePage from './TabTimeMachine';
import { TabPreviewPage } from './TabPreviewPage';
import { TabRecyclingCenter } from './TabRecyclingCenter';
import TabHandoff from './TabHandoff';
import { ProjectColorPage } from './ProjectColorPage';
import VoiceCommandCenterPage from './VoiceCommandCenter';
import { TabPredictionEngine } from './TabPredictionEngine';

/**
 * Tabs hub — one place for every tab-centric surface.
 *
 * Tabs:
 * - live: full TabMonitor (table, clusters, grid, add tab, group creation, search + filters).
 * - collaboration: TabCollaborationHub.
 * - memory: TabMemory.
 * - time-machine: TabTimeMachine.
 * Preview is opened from Live (`?section=preview`) and is not a top-bar tab.
 * - recycle: TabRecyclingCenter.
 * - handoff: TabHandoff.
 * - colors: ProjectColorPage.
 * - voice: VoiceCommandCenter.
 * - predict: TabPredictionEngine.
 */
export function TabsHubPage() {
  const [searchParams] = useSearchParams();

  // Deep-link shim: `?q=…` is a memory search, so any time a user
  // navigates to the Tabs hub with `?q=…` set on a non-memory section
  // (e.g. the broken `?section=time-machine&q=trinetra` link), we
  // perform an in-place redirect to `?section=memory&q=…` so the
  // Memory page can read the query and pre-fill its search box.
  //
  // We use `<Navigate replace>` (an imperative redirect) rather than
  // a `setSearchParams` effect, because the HubShell below also calls
  // `setSearchParams` to sync its active tab to the URL and the two
  // effects can race, leaving the URL on `?section=memory` while the
  // body still renders the time-machine page.
  const q = searchParams.get('q');
  const section = searchParams.get('section');
  if (section === 'security' || section === 'list') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'live');
    next.set('view', 'list');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  if (section === 'groups') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'live');
    next.set('view', 'clusters');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  if (section === 'search') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'live');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  if (section === 'notifications') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'live');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  if (section === 'stash' || section === 'sleep') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'live');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  // Legacy deep links only (e.g. time-machine?q=foo). Memory uses `?q=` on its own section.
  const legacyMemoryQuerySections = ['time-machine', 'preview'];
  if (q && section && legacyMemoryQuerySections.includes(section)) {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'memory');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }
  if (section === 'version-control') {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'time-machine');
    return <Navigate to={`/tabs?${next.toString()}`} replace />;
  }

  // Preview is reachable from Live and /tab-preview, but not shown in the hub top bar.
  if (section === 'preview') {
    return <TabPreviewPage />;
  }

  const tabs = [
    { id: 'live', label: 'Live', icon: Monitor, render: () => <TabMonitor /> },
    { id: 'collaboration', label: 'Collab', icon: Users, render: () => <TabCollaborationHub /> },
    { id: 'memory', label: 'Memory', icon: Database, render: () => <TabMemoryPage /> },
    { id: 'time-machine', label: 'Time Machine', icon: Clock, render: () => <TabTimeMachinePage /> },
    { id: 'recycle', label: 'Recycle', icon: Trash2, render: () => <TabRecyclingCenter /> },
    { id: 'handoff', label: 'Handoff', icon: Smartphone, render: () => <TabHandoff /> },
    { id: 'colors', label: 'Colors', icon: Palette, render: () => <ProjectColorPage /> },
    { id: 'voice', label: 'Voice', icon: Mic, render: () => <VoiceCommandCenterPage /> },
    { id: 'predict', label: 'Predict', icon: Sparkles, render: () => <TabPredictionEngine /> },
  ];

  return (
    <HubShell
      title="Tabs"
      description="Monitor, group, search, secure, and recover your browser tabs."
      icon={Monitor}
      tabs={tabs}
      defaultSection="live"
    />
  );
}

/**
 * Sub-route page for /tabs/workflows — the tab workflow automation builder.
 * Lives outside the HubShell because it has a different primary action.
 */
import { TabWorkflowAutomation } from './TabWorkflowAutomation';
export function TabsWorkflowsPage() {
  return <TabWorkflowAutomation />;
}

export default TabsHubPage;
